import Vue from 'vue'
import App from './App.vue'
import router from './router'
import { googleLoginWithCode } from './api'
import { saveTermsAgreementStatus } from './utils/termsAgreementStorage'
import { pushProductionDataLayerEvent } from './utils/googleTagManagerEvents'
Vue.config.productionTip = false

/**
 * 从 URL 中提取 Google 登录 code（在 Vue 应用初始化之前执行）
 */
function extractGoogleCodeFromUrl() {
  if (typeof window === 'undefined') return null
  
  // 从 window.location.search 获取（code 在 hash 之前的情况）
  const searchParams = new URLSearchParams(window.location.search || '')
  let code = searchParams.get('code')
  if (code) return code
  
  // 从完整 URL 中用正则提取（兜底）
  const fullUrl = window.location.href
  const codeMatch = fullUrl.match(/[?&]code=([^&#]+)/)
  if (codeMatch && codeMatch[1]) {
    return codeMatch[1]
  }
  
  return null
}

/**
 * 处理 Google 登录 code
 */
async function handleGoogleLoginCode() {
  const code = extractGoogleCodeFromUrl()
  if (!code) return false
  
  // 检查是否已经处理过
  if (sessionStorage.getItem('googleCodeHandled') === code) {
    return false
  }
  
  sessionStorage.setItem('googleCodeHandled', code)
  
  try {
    const res = await googleLoginWithCode(code)
    
    if (res && res.status === 200 && res.data) {
      // 保存登录信息
      const data = res.data
      localStorage.setItem('token', data.token)
      localStorage.setItem('username', data.username)
      localStorage.setItem('authority', String(data.authority || '0'))
      // 每次登录都应该使用接口返回的email，如果为null则清除之前的邮箱缓存
      // 规范化email处理，与LoginView.vue保持一致
      const normalizeEmail = (val) => {
        if (val === null || val === undefined) return '';
        const str = String(val).trim();
        return (str === 'null' || str === 'undefined') ? '' : str;
      };
      const emailFromResponse = normalizeEmail(data.email);
      if (emailFromResponse) {
        localStorage.setItem('email', emailFromResponse);
      } else {
        // 接口返回的email为null，清除之前账号的邮箱缓存
        localStorage.removeItem('email');
      }
      if (data.all !== undefined) localStorage.setItem('all', data.all)
      if (data.read !== undefined) localStorage.setItem('read', data.read)
      const userTypeRaw =
        data.userType !== undefined && data.userType !== null
          ? String(data.userType).trim()
          : ''
      if (userTypeRaw) {
        localStorage.setItem('userType', userTypeRaw)
      } else {
        localStorage.removeItem('userType')
      }
      // availableItem 现在是权限配置 JSON（四个命名空间），直接存原始 JSON 字符串；对象则序列化
      const availableItemRaw =
        data.availableItem !== undefined && data.availableItem !== null
          ? data.availableItem
          : data['available-item'] !== undefined && data['available-item'] !== null
            ? data['available-item']
            : data.available_item !== undefined && data.available_item !== null
              ? data.available_item
              : data.availableItems
      if (availableItemRaw !== undefined && availableItemRaw !== null && String(availableItemRaw).trim()) {
        localStorage.setItem('availableItem',
          typeof availableItemRaw === 'string' ? availableItemRaw : JSON.stringify(availableItemRaw))
      } else {
        localStorage.removeItem('availableItem')
      }
      saveTermsAgreementStatus(data.termsAgreement)
      sessionStorage.setItem('loginStatus', 'true')
      if (data.isNewUser === true) {
        pushProductionDataLayerEvent('form_realcg-account_success')
      }
      
      // 清除 URL 中的 code 参数
      const url = new URL(window.location.href)
      url.searchParams.delete('code')
      url.searchParams.delete('scope')
      url.searchParams.delete('authuser')
      url.searchParams.delete('prompt')
      
      // 跳转到 projects 页面
      window.location.href = url.origin + '/projects'
      return true
    }
    
    sessionStorage.removeItem('googleCodeHandled')
    return false
  } catch (error) {
    sessionStorage.removeItem('googleCodeHandled')
    return false
  }
}
// swiper
// import "swiper/css/swiper.css"
// import 'swiper/dist/js/swiper'
// import 'swiper/dist/css/swiper.css'
// import Swiper from "swiper"
// elementui
import ElementUI from 'element-ui';
import '@/styles/element-theme.scss';
import '@/styles/ikkodate-global.scss';
import '@/styles/touch-targets.scss';
Vue.use(ElementUI, { i18n: (key, value) => String(i18n.t(key, value)) });
// video
import Video from 'video.js'
import 'video.js/dist/video-js.css'
Vue.prototype.$video=Video
// CAD
// import Mx from "mxdraw"
// 引入echarts
import * as echarts from 'echarts';
//vue全局注入echarts
Vue.prototype.$echarts = echarts;

//导入图片预览viewer组件以及css
import Viewer from 'v-viewer'
import 'viewerjs/dist/viewer.css'
Vue.use(Viewer);
Viewer.setDefaults({
  Options: { "inline": true, "button": true, "navbar": true, "title": true, "toolbar": true, "tooltip": true, "movable": true, "zoomable": true, "rotatable": true, "scalable": true, "transition": true, "fullscreen": true, "keyboard": true, "url": "data-source" }
});

import moment from 'moment'
Vue.prototype.$moment = moment
import i18n, { setLocaleAsync } from './i18n';
import { DEFAULT_LOCALE } from './i18n/config';
import { getInitialLocale } from './i18n/locale-utils';
import { $st, I18N_KEYS } from './i18n/safe-translate';

// 添加类型安全的翻译函数和翻译 key 常量
Vue.prototype.$st = $st
Vue.prototype.$I18N_KEYS = I18N_KEYS

/**
 * 在应用挂载前，确保初始语言包已加载
 */
async function bootstrap() {
  // 优先处理 Google 登录 code（在 Vue 应用初始化之前）
  const googleLoginHandled = await handleGoogleLoginCode()
  if (googleLoginHandled) {
    // 如果 Google 登录成功，页面会跳转，不需要继续初始化
    return
  }

  const initialLocale = getInitialLocale()

  try {
    await setLocaleAsync(initialLocale)
  } catch (error) {
    console.error('[main] Failed to load initial locale, falling back to default:', error)
    if (initialLocale !== DEFAULT_LOCALE) {
      // 统一走懒加载 API；已优化为若默认语言已内置则跳过加载
      await setLocaleAsync(DEFAULT_LOCALE)
    }
  }

  // 添加全局方法，方便组件使用
  Vue.prototype.$setLocaleAsync = setLocaleAsync

  new Vue({
    router,
    i18n,
    render: h => h(App),
  }).$mount('#app')
}

bootstrap()
